'use client';

import {
  Button,
  Typography,
  TypographyMods,
  TypographySizes,
} from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';
import Link from 'next/link';
import { useMemo } from 'react';
import { useBreakpoints } from 'src/hooks/useBreakpoints';

import { NextImage } from '~components/NextImage/NextImage';
import { ServiceLabel } from '~components/ServiceLabel/ServiceLabel';
import { ServiceLabelMods } from '~components/ServiceLabel/ServiceLabel.types';
import { getDetailedDateTime } from '~helpers/date/getDate';
import IconArrowRight from '~public/static/images/icons/Arrow_right.svg';

import styles from './EventCard.module.scss';
import { EventCardMods, EventCardProps } from './EventCard.types';

export const EventCard = ({
  date,
  hasForm = false,
  formats,
  timezone,
  title,
  url,
  image,
  mod = EventCardMods.Black,
  type,
  services,
}: EventCardProps) => {
  const [, { isMobile, isTablet, isDesktop }] = useBreakpoints();
  const { day, month, time } = getDetailedDateTime(new Date(date), {
    timeZone: 'Europe/Moscow',
  });
  const serviceLabelsMods = useMemo(() => {
    if (mod === EventCardMods.Black) {
      return [ServiceLabelMods.Dark];
    }

    return undefined;
  }, [mod]);

  function Labels() {
    if (hasForm && isDesktop) {
      return (
        <div className={styles.labels}>
          <span className={styles.label}>
            {day} {month} | {time} {timezone}
          </span>
          <span className={classNames(styles.label, styles.labelFormat)}>
            {formats.join(' + ')}
          </span>
          <span className={styles.label}>{type}</span>
        </div>
      );
    }

    return (
      <div className={styles.labels}>
        <Typography
          tagName={TypographySizes.span}
          mods={[TypographyMods.captionSAlt]}
          className={styles.label}
        >
          {type}
        </Typography>
        <span className={classNames(styles.label, styles.labelIcon)}>
          <IconArrowRight />
        </span>
        <Typography
          tagName={TypographySizes.span}
          mods={[TypographyMods.captionSAlt]}
          className={classNames(styles.label, styles.labelFormat)}
        >
          {formats.join(' + ')}
        </Typography>
      </div>
    );
  }

  return (
    <article
      className={classNames(styles.wrapper, mod && styles[mod], {
        [styles.hasForm]: hasForm,
      })}
    >
      {(!hasForm || !isDesktop) && (
        <div className={styles.head}>
          <time dateTime={new Date(date).toISOString()} className={styles.date}>
            <div className={styles.dateDay}>{day}</div>
            <div className={styles.dateMonth}>{month}</div>
            <div className={styles.dateTime}>
              {time} {timezone}
            </div>
          </time>
          {image && (
            <div className={styles.image}>
              <NextImage {...image} className={styles.img} />
            </div>
          )}
          {isTablet && <Labels />}
        </div>
      )}
      {image && hasForm && isDesktop && (
        <div className={styles.imageContainer}>
          <NextImage {...image} className={styles.img} />
        </div>
      )}
      <div className={styles.body}>
        {!isTablet && <Labels />}
        <Typography
          tagName={TypographySizes.h3}
          mods={[TypographyMods.titleXl]}
          className={styles.title}
        >
          {title}
        </Typography>
        <div className={styles.footer}>
          {services && (
            <ul className={styles.services}>
              {services.map((service) => (
                <li key={service.id} className={styles.service}>
                  <ServiceLabel mods={serviceLabelsMods} {...service} />
                </li>
              ))}
            </ul>
          )}
          {isMobile ? (
            <Button
              className={styles.buttonLink}
              href={url}
              text="Подробнее"
              mod="primary"
            />
          ) : (
            <>
              <Link href={url} className={styles.link}>
                Подробнее <IconArrowRight className={styles.linkIcon} />
              </Link>
            </>
          )}
        </div>
      </div>
    </article>
  );
};
